import React from 'react';
import UseCasePageTemplate from '../../../components/UseCasePageTemplate';
import DashboardIntegrityAnimation from '../../../components/DashboardIntegrityAnimation';

const DashboardIntegrityPage = () => {
  // Dashboard icon
  const icon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );

  // Icons for solutions
  const monitorIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );

  const lineageIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  const alertIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );

  // Icons for benefits
  const trustIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );

  const analyticsIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const governanceIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  );

  // Icon for related use cases
  const qualityIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );

  const meshIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const challenges = [
    {
      title: "Broken Reports and Dashboards",
      description: "Data teams spend significant time troubleshooting and fixing broken reports when underlying data changes without notice, leading to business decisions based on incomplete or incorrect information."
    },
    {
      title: "Limited Visibility Into Data Lineage",
      description: "Understanding the complete data journey from source to dashboard is difficult, making it challenging to identify the root cause of data issues that appear in reports."
    },
    {
      title: "Growing Dashboard Sprawl",
      description: "As organizations create more dashboards and reports, maintaining quality across all of them becomes increasingly complex and resource-intensive."
    },
    {
      title: "Eroding Trust in Business Intelligence",
      description: "When reports contain errors or inconsistencies, business users lose confidence in the data team and begin developing their own shadow analytics solutions."
    }
  ];

  const solutions = [
    {
      icon: monitorIcon,
      title: "BI Dashboard Monitoring",
      description: "Automatically track the health and performance of all your dashboards and reports across popular BI tools to detect issues before users do.",
      items: [
        "Comprehensive monitoring for Tableau, Looker, PowerBI, and other BI platforms",
        "Automated detection of stale dashboards and reports",
        "Dashboard usage and performance tracking",
        "Query performance monitoring and optimization recommendations",
        "Unified visibility across all visualization platforms"
      ]
    },
    {
      icon: lineageIcon,
      title: "End-to-End Data Lineage",
      description: "Trace data from source to dashboards with comprehensive lineage that helps pinpoint exactly where issues originate in your data pipeline.",
      items: [
        "Visual lineage mapping from source to visualization",
        "Impact analysis for proposed data changes",
        "Dependency mapping between data assets and reports",
        "Ownership and stewardship tracking",
        "Historical lineage views for understanding changes over time"
      ]
    },
    {
      icon: alertIcon,
      title: "Proactive Dashboard Health Alerts",
      description: "Receive timely notifications about potential dashboard issues before they impact business users with customizable alerting systems.",
      items: [
        "Real-time alerts for dashboard data refresh failures",
        "Notifications for upstream data quality issues affecting reports",
        "Alert routing to appropriate dashboard owners",
        "Customizable alerting thresholds and sensitivity",
        "Automatic incident creation and tracking"
      ]
    }
  ];

  const benefits = [
    {
      icon: trustIcon,
      title: "Trusted Decision-Making",
      description: "Ensure business decisions are based on reliable, up-to-date information by maintaining the integrity of your dashboards and reports."
    },
    {
      icon: analyticsIcon,
      title: "Reduced Analytics Downtime",
      description: "Minimize disruptions to business operations by identifying and resolving dashboard issues before they impact critical decision-making processes."
    },
    {
      icon: governanceIcon,
      title: "Improved Data Governance",
      description: "Strengthen data governance by maintaining clear lineage, ownership, and quality standards across your reporting ecosystem."
    }
  ];

  const relatedUsesCases = [
    {
      icon: qualityIcon,
      title: "Data quality monitoring and testing",
      description: "Deploy and manage monitors and tests on one platform.",
      href: "/use-cases/data-quality"
    },
    {
      icon: meshIcon,
      title: "Data mesh and self serve data",
      description: "Empower data producers and consumers to self-serve.",
      href: "/use-cases/dataops-automation"
    }
  ];

  return (
    <UseCasePageTemplate
      title="Report & Dashboard Integrity"
      description="Ensure your dashboards are accurate, reliable, and up-to-date with Qupid's monitoring and testing capabilities."
      heroImage="/images/use-cases/dashboard-integrity-hero.svg"
      icon={icon}
      heroAnimation={<DashboardIntegrityAnimation />}
      challenges={challenges}
      solutions={solutions}
      benefits={benefits}
      relatedUsesCases={relatedUsesCases}
      additionalContent={
        <div className="bg-gray-50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4">End-to-End Report Reliability</h3>
          <p className="mb-4">
            Qupid's dashboard integrity features create a continuous monitoring system that spans your entire data pipeline, from source systems to final visualizations.
          </p>
          <p>
            By connecting data quality monitoring with dashboard health tracking through lineage, you can immediately understand how upstream changes affect downstream reports and take proactive action to maintain consistent, reliable business intelligence.
          </p>
        </div>
      }
    />
  );
};

export default DashboardIntegrityPage; 